import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, text, type Invoice, type Me, type Visit } from './api.js';

/**
 * What a customer sees.
 *
 * One page, in the order somebody actually cares about it: **is somebody coming, and when** —
 * then what is owed, then the conversation. A customer opens this two or three times a year and
 * almost always with one question, so the answer to that question is above everything else and
 * nothing is behind a tab.
 *
 * The vocabulary is theirs, not the system's. No "appointment status", no "outcome", no job
 * numbers unless they are the only identifier available. A visit is *un rendez-vous*, and a
 * technician on the way is *en route* — the word the technician tapped.
 */
export function App({ onSignedOut }: { onSignedOut: () => void }) {
  const [data, setData] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState<Visit | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.me());
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) return onSignedOut();
      setError('Impossible de charger votre dossier. Réessayez dans un instant.');
    }
  }, [onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * A visit in progress is the one moment this page is watched rather than read, so it refreshes
   * while somebody is on the way. Sixty seconds, and only then — polling a page nobody is
   * looking at is a cost with no reader.
   */
  const live = useMemo(
    () => data?.visits.some((visit) => ['en_route', 'delayed', 'arrived', 'working'].includes(visit.state)) ?? false,
    [data],
  );
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(timer);
  }, [live, load]);

  if (error) return <p className="error page">{error}</p>;
  if (!data) return <div className="loading" aria-busy="true" aria-label="Chargement" />;

  const upcoming = data.visits.filter((visit) => visit.state !== 'done' && visit.state !== 'not_done' && visit.state !== 'cancelled');
  const past = data.visits.filter((visit) => !upcoming.includes(visit));
  const owing = data.invoices.filter((row) => Number(row.balance) > 0);
  const unread = data.notifications.filter((row) => !row.readAt);

  return (
    <div className="app">
      <header className="topbar">
        <span className="topbar__business">{data.business.name}</span>
        <button
          type="button"
          className="ghost small"
          onClick={async () => {
            await api.logout();
            onSignedOut();
          }}
        >
          Quitter
        </button>
      </header>

      <main>
        <h1>Bonjour {data.me.displayName.split(' ')[0]}</h1>

        {unread.length > 0 && (
          <section className="news" aria-label="Nouvelles">
            {unread.map((row) => (
              <p key={row.id}>
                <strong>{row.title}</strong>
                {row.body ? ` — ${row.body}` : ''}
              </p>
            ))}
            <button type="button" className="ghost small" onClick={() => void api.markRead().then(load)}>
              Compris
            </button>
          </section>
        )}

        {/* The question everybody opens this to answer. */}
        <section aria-labelledby="next">
          <h2 id="next">Vos rendez-vous</h2>
          {upcoming.length === 0 && <p className="muted">Aucun rendez-vous prévu pour le moment.</p>}
          {upcoming.map((visit) => (
            <VisitCard key={visit.id} visit={visit} onReschedule={() => setRescheduling(visit)} onChanged={load} />
          ))}
        </section>

        {owing.length > 0 && (
          <section aria-labelledby="owing">
            <h2 id="owing">À payer</h2>
            {owing.map((row) => (
              <button key={row.id} type="button" className="card card--tap" onClick={() => setInvoice(row)}>
                <span className="card__lead">
                  <strong>{money(row.balance)}</strong>
                  <span className="muted">Facture {row.number}</span>
                </span>
                <span className="muted">{row.dueOn ? `Échéance ${date(row.dueOn)}` : 'Voir le détail'}</span>
              </button>
            ))}
          </section>
        )}

        <Conversation messages={data.messages} onSent={load} />

        {past.length > 0 && (
          <section aria-labelledby="past">
            <h2 id="past">Déjà fait</h2>
            <ul className="history">
              {past.slice(0, 8).map((visit) => (
                <li key={visit.id}>
                  <span>{date(visit.startsAt)}</span>
                  <span>{visit.services.map((service) => text(service)).join(' · ') || visit.title || '—'}</span>
                  <span className="muted">{visit.state === 'not_done' ? 'non fait' : 'terminé'}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <Preferences preferences={data.preferences} hasPhone={true} onSaved={load} />
      </main>

      {rescheduling && (
        <RescheduleSheet
          visit={rescheduling}
          onClose={() => setRescheduling(null)}
          onDone={async () => {
            setRescheduling(null);
            await load();
          }}
        />
      )}
      {invoice && <InvoiceSheet invoice={invoice} onClose={() => setInvoice(null)} />}
    </div>
  );
}

/**
 * One visit.
 *
 * The state is the loud part when there is one — *en route* is why somebody has this page open —
 * and quiet otherwise. The window is rendered through its precision: a visit promised as
 * "mercredi AM" says so, because telling a customer 08:00 when the business promised a morning
 * is how they end up waiting from eight and angry by ten.
 */
function VisitCard({ visit, onReschedule, onChanged }: { visit: Visit; onReschedule: () => void; onChanged: () => void }) {
  const live = ['en_route', 'delayed', 'arrived', 'working'].includes(visit.state);

  const status =
    visit.state === 'en_route'
      ? `${visit.technician ?? 'Votre technicien'} est en route`
      : visit.state === 'delayed'
        ? `Retard — arrivée vers ${clock(visit.etaAt)}`
        : visit.state === 'arrived' || visit.state === 'working'
          ? `${visit.technician ?? 'Votre technicien'} est sur place`
          : null;

  return (
    <article className={`card${live ? ' card--live' : ''}`}>
      {status && <p className="card__status">{status}</p>}

      <p className="card__when">{whenLabel(visit)}</p>
      {visit.services.length > 0 && <p className="card__what">{visit.services.map((s) => text(s)).join(' · ')}</p>}
      {visit.address && (
        <p className="muted">
          {visit.address.line1}
          {visit.address.city ? `, ${visit.address.city}` : ''}
        </p>
      )}
      {visit.technician && !status && <p className="muted">Technicien : {visit.technician}</p>}

      {visit.rescheduleRequestId ? (
        <div className="card__note">
          <p>Vous avez demandé à déplacer ce rendez-vous. L’entreprise vous revient là-dessus.</p>
          <button
            type="button"
            className="ghost small"
            onClick={async () => {
              await api.cancelReschedule(visit.id);
              onChanged();
            }}
          >
            Annuler la demande
          </button>
        </div>
      ) : (
        // Not offered once somebody is on the way: asking to move a visit whose technician is in
        // the driveway is a telephone call, not a form.
        !live && (
          <button type="button" className="ghost" onClick={onReschedule}>
            Demander un autre moment
          </button>
        )
      )}
    </article>
  );
}

/**
 * Asking to move it.
 *
 * A preference, not a slot. Offering a calendar of apparently free times would be showing an
 * availability nobody has checked — so this asks what suits, and a person at the office finds
 * something real.
 */
function RescheduleSheet({ visit, onClose, onDone }: { visit: Visit; onClose: () => void; onDone: () => void }) {
  const [when, setWhen] = useState<'asap' | 'range'>('asap');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [period, setPeriod] = useState<string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Sheet title="Demander un autre moment" onClose={onClose}>
      <p className="muted">
        Ce rendez-vous est prévu {whenLabel(visit).toLowerCase()}. Dites-nous ce qui vous conviendrait — quelqu’un
        vous revient avec une nouvelle heure.
      </p>

      <div className="chips">
        <button type="button" className={when === 'asap' ? 'chip on' : 'chip'} onClick={() => setWhen('asap')}>
          Dès que possible
        </button>
        <button type="button" className={when === 'range' ? 'chip on' : 'chip'} onClick={() => setWhen('range')}>
          Entre deux dates
        </button>
      </div>

      {when === 'range' && (
        <div className="row">
          <label className="field">
            <span>À partir du</span>
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </label>
          <label className="field">
            <span>Jusqu’au</span>
            <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </label>
        </div>
      )}

      <fieldset className="chips chips--fieldset">
        <legend>Moment de la journée</legend>
        {([
          ['', 'Peu importe'],
          ['am', 'Avant-midi'],
          ['pm', 'Après-midi'],
          ['evening', 'En soirée'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={period === value ? 'chip on' : 'chip'}
            onClick={() => setPeriod(value)}
          >
            {label}
          </button>
        ))}
      </fieldset>

      <label className="field">
        <span>Autre chose à nous dire ?</span>
        <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
      </label>

      {error && <p className="error">{error}</p>}

      <button
        type="button"
        className="primary"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await api.reschedule(visit.id, {
              preferredFrom: when === 'range' && from ? from : null,
              preferredTo: when === 'range' && to ? to : null,
              preferredPeriod: period || null,
              note: note.trim() || null,
            });
            onDone();
          } catch (caught) {
            setError(caught instanceof ApiError ? caught.message : 'Impossible d’envoyer la demande.');
          } finally {
            setBusy(false);
          }
        }}
      >
        Envoyer la demande
      </button>
      <p className="fineprint">
        Le rendez-vous reste au calendrier tant que l’entreprise ne l’a pas déplacé.
      </p>
    </Sheet>
  );
}

/** An invoice, and how to pay it. */
function InvoiceSheet({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  return (
    <Sheet title={`Facture ${invoice.number}`} onClose={onClose}>
      <ul className="lines">
        {invoice.items.map((item, index) => (
          <li key={index}>
            <span>
              {item.name}
              {item.quantity > 1 ? ` ×${item.quantity}` : ''}
            </span>
            <span className="tnum">{money(item.total)}</span>
          </li>
        ))}
      </ul>
      <p className="total">
        <span>Solde</span>
        <strong className="tnum">{money(invoice.balance)}</strong>
      </p>
      {invoice.note && <p className="muted">{invoice.note}</p>}

      <h3>Comment payer</h3>
      {invoice.paymentToken ? (
        <a className="primary" href={`/payer/${invoice.paymentToken}`}>
          Payer maintenant
        </a>
      ) : (
        /*
         * No live link. Said plainly rather than shown as a broken button: online payment is not
         * configured yet, and the two ways these businesses are actually paid are on the invoice
         * itself.
         */
        <p className="muted">
          Le paiement en ligne n’est pas encore offert. Vous pouvez payer par virement Interac ou par chèque —
          les coordonnées sont sur votre facture. Écrivez-nous ci-dessous si vous avez une question.
        </p>
      )}
    </Sheet>
  );
}

/**
 * The conversation.
 *
 * The same thread the office and the technician read — which is the point of it being here
 * rather than an email address. A customer who was told nine o'clock can see that they were.
 */
function Conversation({ messages, onSent }: { messages: Me['messages']; onSent: () => void }) {
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const ordered = [...messages].reverse();

  return (
    <section aria-labelledby="talk">
      <h2 id="talk">Écrivez-nous</h2>

      {ordered.length > 0 && (
        <ol className="thread">
          {ordered.slice(-20).map((message) => (
            <li key={message.id} className={message.authorKind === 'customer' ? 'mine' : 'theirs'}>
              {message.authorKind === 'staff' && message.authorName && (
                <span className="thread__who">{message.authorName}</span>
              )}
              {message.body && <p>{message.body}</p>}
              {message.attachments.map((attachment) => (
                <img key={attachment.id} src={`/api/client/media/${attachment.id}`} alt="" loading="lazy" />
              ))}
              <span className="thread__when">{dateTime(message.sentAt)}</span>
            </li>
          ))}
        </ol>
      )}

      <div className="composer">
        <textarea
          rows={3}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Une question, une précision, l’endroit où se stationner…"
        />
        {pending.length > 0 && <p className="muted">{pending.length} photo(s) jointe(s)</p>}
        <div className="composer__actions">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            hidden
            onChange={async (event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              try {
                const attachmentId = await api.upload(file);
                setPending((current) => [...current, attachmentId]);
              } catch (caught) {
                setError(caught instanceof ApiError ? caught.message : 'Photo non envoyée.');
              }
            }}
          />
          <button type="button" className="ghost" onClick={() => fileRef.current?.click()}>
            Joindre une photo
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || (!draft.trim() && pending.length === 0)}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.send(draft.trim() || 'Photo', pending);
                setDraft('');
                setPending([]);
                onSent();
              } catch (caught) {
                setError(caught instanceof ApiError ? caught.message : 'Message non envoyé.');
              } finally {
                setBusy(false);
              }
            }}
          >
            Envoyer
          </button>
        </div>
        {error && <p className="error">{error}</p>}
      </div>
    </section>
  );
}

/** How they would like to be told things. Theirs to change. */
function Preferences({
  preferences,
  hasPhone,
  onSaved,
}: {
  preferences: Me['preferences'];
  hasPhone: boolean;
  onSaved: () => void;
}) {
  const [state, setState] = useState(preferences);

  const save = async (next: Partial<Me['preferences']>) => {
    const merged = { ...state, ...next };
    setState(merged);
    await api.preferences(next);
    onSaved();
  };

  return (
    <section aria-labelledby="prefs" className="prefs">
      <h2 id="prefs">Vous prévenir</h2>
      <label className="switch">
        <input
          type="checkbox"
          checked={state.notifyOnRoute}
          onChange={(event) => void save({ notifyOnRoute: event.target.checked })}
        />
        <span>Quand le technicien part vers chez moi</span>
      </label>
      <label className="switch">
        <input
          type="checkbox"
          checked={state.notifyInvoice}
          onChange={(event) => void save({ notifyInvoice: event.target.checked })}
        />
        <span>Quand une facture est prête</span>
      </label>
      <label className="switch">
        <input
          type="checkbox"
          checked={state.notifyMessage}
          onChange={(event) => void save({ notifyMessage: event.target.checked })}
        />
        <span>Quand l’entreprise m’écrit</span>
      </label>

      {hasPhone && (
        <div className="chips">
          <button
            type="button"
            className={state.channel === 'sms' ? 'chip on' : 'chip'}
            onClick={() => void save({ channel: 'sms' })}
          >
            Par texto
          </button>
          <button
            type="button"
            className={state.channel === 'email' ? 'chip on' : 'chip'}
            onClick={() => void save({ channel: 'email' })}
          >
            Par courriel
          </button>
        </div>
      )}
    </section>
  );
}

function Sheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="scrim" onClick={onClose}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>{title}</h2>
          <button type="button" className="ghost small" onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </header>
        <div className="sheet__body">{children}</div>
      </div>
    </div>
  );
}

// --- saying when -------------------------------------------------------------------------
//
// Read through `precision`, always. A booking promised as a half-day is a half-day; printing a
// time nobody committed to is how a customer waits from eight and is angry by ten.

const PERIODS: Record<string, string> = {
  am: 'en avant-midi',
  pm: 'en après-midi',
  day: 'dans la journée',
  evening: 'en soirée',
  week: 'cette semaine',
};

function whenLabel(visit: Visit): string {
  if (!visit.startsAt) return 'Date à confirmer';
  const day = new Date(visit.startsAt).toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' });
  if (visit.precision === 'period') return `${cap(day)} ${PERIODS[visit.period ?? 'day'] ?? ''}`.trim();
  if (visit.precision === 'day') return cap(day);
  return `${cap(day)} à ${clock(visit.startsAt)}`;
}

const cap = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);
const clock = (value: string | null): string =>
  value ? new Date(value).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' }) : '—';
const date = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' }) : '—';
const dateTime = (value: string): string =>
  new Date(value).toLocaleString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const money = (value: string): string =>
  new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(Number(value) || 0);
